import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { UserSelect, useMembers } from '../ui/UserSelect';
import { SprintReportPanel } from './SprintReport';
import {
  completeSprint,
  createSprint,
  getProject,
  startSprint,
  updateProject,
  type ProjectDetail,
  type ProjectHealth,
  type ProjectPriority,
  type ProjectStatus,
  type Sprint,
} from './api';

/**
 * One project, and the sprints inside it (Phase 152).
 *
 * The product could list projects and it could plan a backlog, but there was nowhere that was *the
 * project*: to change who owns it you edited a row in a table, to see its sprints you went to a
 * different screen, and how much of it was done was a question nothing answered. This is the page a
 * person opens when somebody asks "how is that project going".
 *
 * Four things, in the order the question is usually asked:
 *
 * - **Where it stands** — status, health, owner, dates, and a completion counted from the tasks
 * - **What is late** — because a project at 80% with nine overdue tasks is not at 80%
 * - **The sprints** — each with its number, window, progress, and the ceremony it is waiting for
 * - **The way into the work** — the backlog and the board, which already existed and are linked
 *
 * Health is its own field rather than a value of status, because "active" and "going to land" are
 * different questions and one field answering both is a field nobody fills in honestly.
 */

const HEALTH_TONE: Record<ProjectHealth, BadgeTone> = {
  ON_TRACK: 'positive',
  AT_RISK: 'warning',
  OFF_TRACK: 'danger',
};

const STATUSES: ProjectStatus[] = ['ACTIVE', 'ON_HOLD', 'COMPLETED', 'ARCHIVED'];
const HEALTHS: ProjectHealth[] = ['ON_TRACK', 'AT_RISK', 'OFF_TRACK'];
const PRIORITIES: ProjectPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const SPRINT_TONE: Record<string, BadgeTone> = {
  PLANNED: 'neutral',
  ACTIVE: 'info',
  COMPLETED: 'positive',
};

/** Whole percent, and zero tasks is zero rather than a division by nothing. */
function percentOf(done: number, total: number): number {
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

export function ProjectPage(): React.JSX.Element {
  const { projectId = '' } = useParams();
  const client = useQueryClient();
  const { options: people } = useMembers();
  const [error, setError] = useState<string | null>(null);

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId),
    enabled: projectId !== '',
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['project', projectId] });
    void client.invalidateQueries({ queryKey: ['projects'] });
  };

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof updateProject>[1]) => updateProject(projectId, patch),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const data = project.data;

  if (project.error) {
    return (
      <div className="view">
        <ErrorState error={project.error} onRetry={() => void project.refetch()} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="view">
        <Loading />
      </div>
    );
  }

  const done = percentOf(data.progress.done, data.progress.tasks);

  return (
    <div className="view">
      <PageHeader
        title={data.key ? `${data.key} · ${data.name}` : data.name}
        description={data.description ?? t('work.projectDescription')}
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel title={t('work.whereItStands')}>
        <div className="project-grid">
          <label className="field">
            <span className="contact-field__label">{t('crm.field.status')}</span>
            <select
              className="select"
              value={data.status}
              disabled={save.isPending}
              onChange={(event) => save.mutate({ status: event.target.value as ProjectStatus })}
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`work.status.${status}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="contact-field__label">{t('work.priority')}</span>
            <select
              className="select"
              value={data.priority}
              disabled={save.isPending}
              onChange={(event) => save.mutate({ priority: event.target.value as ProjectPriority })}
            >
              {PRIORITIES.map((priority) => (
                <option key={priority} value={priority}>
                  {t(`work.priority.${priority}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="contact-field__label">{t('work.owner')}</span>
            <UserSelect
              value={data.owner_id}
              options={people}
              onChange={(next) => save.mutate({ ownerId: next })}
              placeholder={t('work.noOwner')}
              emptyLabel={t('work.noOwner')}
            />
          </label>

          <label className="field">
            <span className="contact-field__label">{t('work.startDate')}</span>
            <input
              type="date"
              className="input"
              defaultValue={data.start_date ?? ''}
              disabled={save.isPending}
              onBlur={(event) => {
                const next = event.target.value || null;
                if (next !== data.start_date) save.mutate({ startDate: next });
              }}
            />
          </label>

          <label className="field">
            <span className="contact-field__label">{t('work.due')}</span>
            <input
              type="date"
              className="input"
              defaultValue={data.due_date ?? ''}
              disabled={save.isPending}
              onBlur={(event) => {
                const next = event.target.value || null;
                if (next !== data.due_date) save.mutate({ dueDate: next });
              }}
            />
          </label>
        </div>

        <Health project={data} onSave={(patch) => save.mutate(patch)} saving={save.isPending} />
      </Panel>

      <Panel title={t('work.progress')}>
        <div className="project-progress">
          <div className="project-progress__bar" aria-hidden="true">
            <span style={{ inlineSize: `${done}%` }} />
          </div>

          <p className="strong">
            {t('work.doneOf')
              .replace('{done}', String(data.progress.done))
              .replace('{total}', String(data.progress.tasks))
              .replace('{percent}', String(done))}
          </p>

          {/* The number that changes what somebody does next, so it is not buried in the bar. */}
          {data.progress.overdue > 0 && (
            <Link to={`/trabalho/tarefas?projeto=${data.id}`} className="alert alert--compact">
              {t('work.overdueTasks').replace('{n}', String(data.progress.overdue))}
            </Link>
          )}

          {data.progress.tasks === 0 && <p className="muted small">{t('work.noTasksYet')}</p>}
        </div>
      </Panel>

      {data.uses_sprints ? (
        <Sprints project={data} onChanged={refresh} onError={setError} />
      ) : (
        <Panel title={t('work.sprints')}>
          <p className="muted small">{t('work.sprintsOff')}</p>

          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={save.isPending}
            onClick={() => save.mutate({ usesSprints: true })}
          >
            {t('work.turnSprintsOn')}
          </button>
        </Panel>
      )}

      <Panel title={t('work.intoTheWork')}>
        <div className="toolbar">
          <Link className="button button--quiet button--compact" to={`/trabalho/backlog?projeto=${data.id}`}>
            {t('nav.backlog')}
          </Link>
          <Link className="button button--quiet button--compact" to={`/trabalho/tarefas?projeto=${data.id}`}>
            {t('nav.tasks')}
          </Link>
        </div>
      </Panel>
    </div>
  );
}

/**
 * Is this going to land, and who said so.
 *
 * Kept apart from the rest of the fields because it is the one somebody has to think about: a note
 * beside it, and the date it was last touched, so an "at risk" from six weeks ago reads as six
 * weeks old rather than as current.
 */
function Health({
  project,
  onSave,
  saving,
}: {
  project: ProjectDetail;
  onSave: (patch: { health?: ProjectHealth; healthNote?: string | null }) => void;
  saving: boolean;
}): React.JSX.Element {
  const [note, setNote] = useState(project.health_note ?? '');

  return (
    <div className="project-health">
      <div className="toolbar">
        <span className="contact-field__label">{t('work.health')}</span>

        {HEALTHS.map((health) => (
          <button
            key={health}
            type="button"
            className={`chip${project.health === health ? ' chip--active' : ''}`}
            aria-pressed={project.health === health}
            disabled={saving}
            onClick={() => onSave({ health, healthNote: note.trim() || null })}
          >
            {t(`work.health.${health}`)}
          </button>
        ))}

        <Badge tone={HEALTH_TONE[project.health]}>{t(`work.health.${project.health}`)}</Badge>
      </div>

      <label className="field">
        <span className="contact-field__label">{t('work.healthNote')}</span>
        <input
          type="text"
          className="input"
          value={note}
          placeholder={t('work.healthNotePlaceholder')}
          disabled={saving}
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => {
            if ((note.trim() || null) !== project.health_note) {
              onSave({ health: project.health, healthNote: note.trim() || null });
            }
          }}
        />
      </label>

      {project.health_set_at && (
        <p className="muted small">
          {t('work.healthSetAt').replace('{date}', formatDate(project.health_set_at))}
        </p>
      )}
    </div>
  );
}

/** The sprints inside the project, each with its window, its numbers and its next ceremony. */
function Sprints({
  project,
  onChanged,
  onError,
}: {
  project: ProjectDetail;
  onChanged: () => void;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const [adding, setAdding] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const done = (): void => {
    onError(null);
    setAdding(false);
    setName('');
    setGoal('');
    setStartsAt('');
    setEndsAt('');
    onChanged();
  };

  const add = useMutation({
    mutationFn: () =>
      createSprint({
        projectId: project.id,
        name: name.trim(),
        goal: goal.trim() || null,
        startsAt: startsAt ? new Date(startsAt).toISOString() : null,
        endsAt: endsAt ? new Date(endsAt).toISOString() : null,
      }),
    onSuccess: done,
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const begin = useMutation({
    mutationFn: (id: string) => startSprint(id),
    onSuccess: done,
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  /*
   * Closing a sprint sends what is unfinished to the backlog.
   *
   * The API can also move it into the next sprint; that choice belongs on the backlog screen, where
   * somebody can see what they are moving. Offering it here, with the items out of sight, is how
   * work gets carried into a sprint nobody meant to fill.
   */
  const close = useMutation({
    mutationFn: (id: string) => completeSprint(id, null),
    onSuccess: done,
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const running = project.sprints.some((sprint) => sprint.state === 'ACTIVE');

  return (
    <Panel title={t('work.sprints')}>
      {project.sprints.length === 0 ? (
        <p className="muted small">{t('work.noSprints')}</p>
      ) : (
        <ul className="sprint-list">
          {project.sprints.map((sprint) => (
            <SprintRow
              key={sprint.id}
              sprint={sprint}
              // One sprint runs at a time; the server enforces it and the button says so first.
              canStart={sprint.state === 'PLANNED' && !running}
              starting={begin.isPending}
              closing={close.isPending}
              onStart={() => begin.mutate(sprint.id)}
              onClose={() => close.mutate(sprint.id)}
              onReport={() => setReport((current) => (current === sprint.id ? null : sprint.id))}
              reportOpen={report === sprint.id}
            />
          ))}
        </ul>
      )}

      {adding ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) add.mutate();
          }}
        >
          <label className="field">
            <span className="contact-field__label">{t('work.sprintName')}</span>
            <input
              className="input"
              value={name}
              autoFocus
              placeholder={t('work.sprintNamePlaceholder')}
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <label className="field">
            <span className="contact-field__label">{t('work.sprintGoal')}</span>
            <input
              className="input"
              value={goal}
              placeholder={t('work.sprintGoalPlaceholder')}
              onChange={(event) => setGoal(event.target.value)}
            />
          </label>

          <div className="project-grid">
            <label className="field">
              <span className="contact-field__label">{t('work.startDate')}</span>
              <input
                type="date"
                className="input"
                value={startsAt}
                onChange={(event) => setStartsAt(event.target.value)}
              />
            </label>

            <label className="field">
              <span className="contact-field__label">{t('work.endDate')}</span>
              <input
                type="date"
                className="input"
                value={endsAt}
                onChange={(event) => setEndsAt(event.target.value)}
              />
            </label>
          </div>

          <div className="toolbar">
            <button
              type="submit"
              className="button button--primary button--compact"
              disabled={name.trim() === '' || add.isPending}
            >
              {add.isPending ? t('common.saving') : t('work.addSprint')}
            </button>
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setAdding(false)}
            >
              {t('confirm.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => setAdding(true)}
        >
          {t('work.addSprint')}
        </button>
      )}
    </Panel>
  );
}

function SprintRow({
  sprint,
  canStart,
  starting,
  closing,
  onStart,
  onClose,
  onReport,
  reportOpen,
}: {
  sprint: Sprint;
  canStart: boolean;
  starting: boolean;
  closing: boolean;
  onStart: () => void;
  onClose: () => void;
  onReport: () => void;
  reportOpen: boolean;
}): React.JSX.Element {
  const points = percentOf(sprint.progress.pointsDone, sprint.progress.points);

  return (
    <li className="sprint-row">
      <div className="sprint-row__head">
        <span className="strong">
          {t('work.sprintNumber').replace('{n}', String(sprint.number))} · {sprint.name}
        </span>

        <Badge tone={SPRINT_TONE[sprint.state] ?? 'neutral'}>
          {t(`work.sprintState.${sprint.state}`)}
        </Badge>
      </div>

      {sprint.goal && <p className="muted small">{sprint.goal}</p>}

      <p className="muted small">
        {sprint.startsAt && sprint.endsAt
          ? `${formatDate(sprint.startsAt)} → ${formatDate(sprint.endsAt)}`
          : t('work.noWindow')}
      </p>

      <div className="project-progress__bar" aria-hidden="true">
        <span style={{ inlineSize: `${points}%` }} />
      </div>

      <p className="muted small">
        {t('work.sprintProgress')
          .replace('{done}', String(sprint.progress.done))
          .replace('{total}', String(sprint.progress.items))
          .replace('{points}', String(sprint.progress.pointsDone))
          .replace('{allPoints}', String(sprint.progress.points))}
      </p>

      {/* A plan is not a plan while some of it has no estimate, so the count is stated outright. */}
      {sprint.progress.unestimated > 0 && sprint.state !== 'COMPLETED' && (
        <p className="muted small">
          {t('work.unestimated').replace('{n}', String(sprint.progress.unestimated))}
        </p>
      )}

      <div className="toolbar">
        {sprint.state === 'PLANNED' && (
          <button
            type="button"
            className="button button--primary button--compact"
            disabled={!canStart || starting}
            title={canStart ? undefined : t('work.oneSprintAtATime')}
            onClick={onStart}
          >
            {t('work.startSprint')}
          </button>
        )}

        {sprint.state === 'ACTIVE' && (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={closing}
            onClick={onClose}
          >
            {t('work.completeSprint')}
          </button>
        )}

        {sprint.state === 'COMPLETED' && (
          <button
            type="button"
            className="button button--quiet button--compact"
            aria-expanded={reportOpen}
            onClick={onReport}
          >
            {t('work.sprintReport')}
          </button>
        )}
      </div>

      {/* The burndown and the scope ledger, in place — the sprint is the thing being read. */}
      {reportOpen && <SprintReportPanel sprintId={sprint.id} />}
    </li>
  );
}
